import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "@tanstack/react-router";
import {
  OnChangeFn,
  RowSelectionState,
  type ColumnVisibilityState,
} from "@tanstack/react-table";
import { AgeRangeAvailableBounds } from "reedy-data/api";
import { HYBRID_DEFAULT_PAGE_SIZE } from "~/hooks/search/constants";
import {
  type SearchProductWithScore,
  type SearchQuery,
} from "~/hooks/search/types";
import { Text } from "~/components/text";
import { hybridColumns, MATCH_TYPE_COLUMN_ID } from "./hybrid/columns";
import { useHybridBooksTable } from "./hybrid/use-hybrid-books-table";
import { BooksPageTable } from "./books-page-table";
import { BooksPageHeader } from "~/pages/books/components/books-page-header";

export const HybridBooksTable = memo(
  ({
    rowSelection,
    setRowSelection,
    isLoading,
    searchQuery,
    setSearchQuery,
    error,
    products,
    count,
    total,
    fetchAllResults,
    ageRangeAvailableBounds,
  }: {
    rowSelection: RowSelectionState;
    setRowSelection: OnChangeFn<RowSelectionState>;
    isLoading: boolean;
    searchQuery: SearchQuery;
    setSearchQuery: (query: Partial<SearchQuery>) => void;
    error: Error | null | undefined;
    products: SearchProductWithScore[];
    count: number;
    total: number | null;
    fetchAllResults: () => Promise<SearchProductWithScore[]>;
    ageRangeAvailableBounds?: AgeRangeAvailableBounds | null;
  }) => {
    const { t } = useTranslation(["books"]);
    const location = useLocation();

    // Detail back-button label reads location.state.from.
    const linkState = useMemo(() => ({ from: location.href }), [location.href]);

    const tableColumns = useMemo(
      () => hybridColumns(t, { loading: isLoading, linkState }),
      [t, isLoading, linkState],
    );

    const trimmedQuery = searchQuery.query?.trim() ?? "";
    const hasQuery = trimmedQuery.length > 0;
    const columnVisibility: ColumnVisibilityState = useMemo(
      () => ({ [MATCH_TYPE_COLUMN_ID]: hasQuery }),
      [hasQuery],
    );

    // Selection reset key; includes search type so hybrid/quick switches clear selection.
    const resetSelectionKey = JSON.stringify({
      t: searchQuery.type,
      q: searchQuery.query,
      f: searchQuery.filters,
    });

    const { table } = useHybridBooksTable({
      data: products,
      columns: tableColumns,
      totalRecords: count,
      resetSelectionKey,
      columnVisibility,
      rowSelection,
      setRowSelection,
    });

    const isSelectedAll = table.getSelectedAll();
    const selectedProducts = isSelectedAll ? "ALL" : table.getSelectedIds();

    const effectivePageSize = searchQuery.pageSize || HYBRID_DEFAULT_PAGE_SIZE;
    const showEndOfList = hasQuery && count > 0 && !isLoading;
    const showPagination = !hasQuery && count > effectivePageSize;

    return (
      <>
        <BooksPageHeader
          count={count}
          total={total}
          isLoading={isLoading}
          selectedProducts={selectedProducts}
          query={searchQuery.query}
          searchType={searchQuery.type}
        />
        <BooksPageTable
          table={table}
          isLoading={isLoading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          error={error}
          products={products}
          fetchAllResults={fetchAllResults}
          defaultPageSize={HYBRID_DEFAULT_PAGE_SIZE}
          hasPagination={showPagination}
          hasDetails={true}
          hasNoResultsBanner={true}
          ageRangeAvailableBounds={ageRangeAvailableBounds}
        />
        {showEndOfList && (
          <Text
            data-testid="hybrid-end-of-list"
            variant="muted"
            className="text-center py-4"
          >
            {t("books:search.endOfList")}
          </Text>
        )}
      </>
    );
  },
);

HybridBooksTable.displayName = "HybridBooksTable";
